import { useState } from 'react';
import { css } from '@emotion/react';
import { sendTestEmail as sendTestEmailMutation } from '@frontend/api-bulk-messaging';
import { useFormatDynamicFieldAndLabel } from '@frontend/dynamic-fields-hooks';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { Button, useModalControl, useAlert } from '@frontend/design-system';
import { COMPOSER_BREAKPOINTS } from '../../content-composer/constants';
import { useContentComposer } from '../../content-composer/content-composer-provider';
import { useEmailHeader } from './email-header-context';
import { SendTestEmailModal } from './send-test-email-modal';

type Props = {
  subject: string;
};

export const SendTestEmailButton = ({ subject }: Props) => {
  const { t } = useTranslation('email-composer');
  const { renderHtml, selectedLocationId } = useContentComposer();
  const { trackingPrefix } = useEmailHeader();
  const [loading, setLoading] = useState(false);
  const alert = useAlert();
  const { modalProps, triggerProps, closeModal } = useModalControl();
  const { selectedOrgId: orgId } = useAppScopeStore();
  const { convertLabelToDynamicField } = useFormatDynamicFieldAndLabel();

  const sendTestEmail = async (passedEmailAddress: string) => {
    if (!passedEmailAddress) {
      alert.error(t('Please enter an email address.'));
      return;
    }

    setLoading(true);

    try {
      const html = await renderHtml();
      if (!html) return;
      const convertedSubject = subject ? convertLabelToDynamicField(subject) ?? '' : t('Test Email');
      await sendTestEmailMutation({
        orgId,
        to: passedEmailAddress,
        subject: convertedSubject,
        body: html,
        locationId: selectedLocationId,
      });
      alert.success(t('Test email sent!'));
      closeModal();
    } catch (error) {
      alert.error(t('Failed to send test email! Try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant='secondary'
        {...triggerProps}
        css={css`
          height: 100%;
          @media (max-width: ${COMPOSER_BREAKPOINTS.DESKTOP}px) {
            display: none;
          }
        `}
        trackingId={`${trackingPrefix}-send-test-email-btn`}
      >
        {t('Send Test Email')}
      </Button>
      <Button
        variant='secondary'
        {...triggerProps}
        css={css`
          height: 100%;
          width: fit-content;
          @media (min-width: ${COMPOSER_BREAKPOINTS.DESKTOP + 1}px) {
            display: none;
          }
        `}
        trackingId={`${trackingPrefix}-send-test-email-btn`}
      >
        {t('Send Test')}
      </Button>
      <SendTestEmailModal
        hasSubject={!!subject}
        loading={loading}
        modalProps={modalProps}
        sendTestEmail={sendTestEmail}
      />
    </>
  );
};
